import React, { ReactElement, useEffect, useState, forwardRef } from "react";
import moment from "moment";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import { DatePickerHeader } from "./DatePickerHeader/DatePickerHeader";

import "react-datepicker/dist/react-datepicker.css";
import styles from "./CalendarDatePicker.module.scss";
import { BelCalendarIcon } from "@kursk/components/ui/icons.bel";
import { CalendarIcon as CalendarIconVladimir } from "@kursk/components/ui/icons.vladimir";
import { CalendarIcon as CalendarIconPprog } from "@kursk/components/ui/icons.pprog";
import { CalendarIcon as CalendarIconSochi } from "@kursk/components/ui/icons.sochi";
import { CalendarIcon as CalendarIconLnr } from "@kursk/components/ui/icons.lnr";
import { CalendarIcon as CalendarIconAstrakhan } from "@kursk/components/ui/icons.astrakhan";
import { CalendarIcon } from "../icons";
import { regionContent } from "@kursk/content/regionContent";
import { fixTimezoneDiff } from "@kursk/utils/fixTimezoneDiff";
import { themeValue } from "@kursk/themes";

registerLocale('ru', ru);

interface IProps {
  date: number;
  enabledDays: string[];
  minDate?: Date;
  onChangeDate: (date: number) => void;
  customInput?: (props) => ReactElement;
  popperPlacement?: string;
}

export const CalendarDatePicker = forwardRef<ReactElement, IProps>(({
  date,
  minDate,
  enabledDays,
  onChangeDate,
  customInput,
  popperPlacement = 'bottom-end'
}: IProps, ref) => {
  const [selectedDate, setSelectedDate] = useState(
    new Date(`${moment.tz(date, regionContent.timezone).format('YYYY-MM-DDTHH:mm:ss')}Z`)
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const preparedDate = moment.tz(date, regionContent.timezone).format('YYYY-MM-DDTHH:mm:ss')

    setSelectedDate(new Date(`${preparedDate}Z`));

    setIsOpen(false);
  }, [date]);

  const filterDates = (date) => {
    return enabledDays.indexOf(moment.tz(date, regionContent.timezone).format('YYYY-MM-DD')) !== -1;
  };

  const onSubmit = () => {
    const preparedDate = moment(fixTimezoneDiff(selectedDate)).format('YYYY-MM-DD');
    onChangeDate(moment.tz(preparedDate, regionContent.timezone).valueOf())
  };
  const onClose = () => {
    setSelectedDate(new Date(`${moment.tz(date, regionContent.timezone).format('YYYY-MM-DDTHH:mm:ss')}Z`));
    setIsOpen(false)
  }
  return (
    <div className={styles.calendarWrapper}>
      <DatePicker
        ref={ref}
        locale='ru'
        key={date}
        dateFormat="dd MMMM yyyy"
        minDate={minDate}
        selected={selectedDate}
        onChange={(date) => setSelectedDate(date)}
        customInput={customInput && customInput({ isOpen })}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => onClose()}
        disabledKeyboardNavigation
        shouldCloseOnSelect={false}
        filterDate={filterDates}
        renderCustomHeader={DatePickerHeader}
        calendarContainer={({ className, children }) => (
          <div className={className}>
            {children}
            <div className={styles.buttonWrap}>
              <div className={styles.button} onClick={onSubmit}>Применить</div>
            </div>
          </div>
        )}
        dayClassName={() => 'react-datepicker__day--custom'}
        popperPlacement={popperPlacement}
        fixedHeight
      />
      {!customInput && <span className={styles.icon}>
        {themeValue({
          bel: <BelCalendarIcon />,
          vladimir: <CalendarIconVladimir />,
          pprog: <CalendarIconPprog />,
          sochi: <CalendarIconSochi />,
          lnr: <CalendarIconLnr />,
          astrakhan: <CalendarIconAstrakhan />,
          default: <CalendarIcon width={14} />,
        })}
      </span>}
    </div>
  )
})
